import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { MonthData } from "models/ReceiptModels";
import { FC } from "react";
// @ts-ignore
import { Currency } from "react-intl-number-format";
import { generateMonthName } from "../utils/generateMonth";

const ReceiptsAccordion: FC<{ data: MonthData[] | null; error: boolean }> = ({
  data,
  error,
}) => {
  const theme = useTheme();

  if (error) {
    return <Alert severity="error">Failed to load receipts data.</Alert>;
  }

  if (!data) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {(data || []).map((monthData) => (
        <Accordion key={monthData.month} sx={{ p: 1 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${monthData.month}-content`}
            id={`panel-${monthData.month}-header`}
            sx={{
              backgroundColor: "#DFD0B8",
              borderRadius: "0.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                px: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#153448",
                    fontWeight: "bold",
                  }}
                >
                  {generateMonthName(monthData.month)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Total Receipts: {monthData.totalReceipts}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 1,
                }}
              >
                <Typography variant="body2">
                  Paid:{" "}
                  <Typography
                    sx={{
                      display: "inline-block",
                      fontWeight: "bold",
                      color: "#3C5B6F",
                    }}
                  >
                    {monthData.totalPaid}
                  </Typography>{" "}
                  | Unpaid:{" "}
                  <Typography
                    sx={{
                      display: "inline-block",
                      fontWeight: "bold",
                      color: "#3C5B6F",
                    }}
                  >
                    {monthData.totalUnpaid}
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  Amount Given:{" "}
                  <Typography
                    sx={{
                      display: "inline-block",
                      fontWeight: "bold",
                      color: "#3C5B6F",
                    }}
                  >
                    <Currency locale="en-US" currency="TND">
                      {monthData.totalAmountGiven}
                    </Currency>
                  </Typography>{" "}
                   | Amount Received:{" "}
                  <Typography
                    sx={{
                      display: "inline-block",
                      fontWeight: "bold",
                      color: "#3C5B6F",
                    }}
                  >
                    {" "}
                    <Currency locale="en-US" currency="TND">
                      {monthData.totalAmountReceived}
                    </Currency>
                  </Typography>{" "}
                   Difference:{" "}
                  <Typography
                    sx={{
                      display: "inline-block",
                      fontWeight: "bold",
                      color: "#3C5B6F",
                    }}
                  >
                    <Currency locale="en-US" currency="TND">
                      {monthData.totalAmountReceived -
                        monthData.totalAmountGiven}
                    </Currency>
                  </Typography>{" "}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ mt: 1 }}>
            {monthData.days?.length > 0 && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell align="right">Total Receipts</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Unpaid</TableCell>
                      <TableCell align="right">Amount Given</TableCell>
                      <TableCell align="right">Amount Received</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(monthData.days || []).map((dayData) => (
                      <TableRow
                        key={dayData.day}
                        sx={{
                          borderLeft: `solid 2px ${
                            dayData.totalUnpaid > 0
                              ? theme.palette.error.light
                              : dayData.totalPaid > 0
                              ? theme.palette.success.light
                              : "inherit"
                          }`,
                        }}
                      >
                        <TableCell>{dayData.day}</TableCell>
                        <TableCell align="right">
                          {dayData.totalReceipts}
                        </TableCell>
                        <TableCell align="right">{dayData.totalPaid}</TableCell>
                        <TableCell align="right">
                          {dayData.totalUnpaid}
                        </TableCell>
                        <TableCell align="right">
                          {dayData.totalAmountGiven.toFixed(2)} TND
                        </TableCell>
                        <TableCell align="right">
                          {dayData.totalAmountReceived.toFixed(2)} TND
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ReceiptsAccordion;
